import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { CheckResult, MetricSource } from '@shared/types';
import { getCheck } from '../api';
import ScoreBadge from '../components/ScoreBadge';
import ScoreRadar from '../components/ScoreRadar';
import IssueList from '../components/IssueList';
import { scoreColor, formatDate } from '../scoreUtils';

const CWV_SOURCE_LABEL: Record<MetricSource, string> = {
  field: '実測値（フィールドデータ / CrUX）',
  lab: 'ラボ値（Lighthouse推定）',
  none: '取得不可',
};

export default function CheckResultPage() {
  const { checkId } = useParams<{ checkId: string }>();
  const [result, setResult] = useState<CheckResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!checkId) return;
    let alive = true;
    (async () => {
      try {
        const s = await getCheck(checkId);
        if (!alive) return;
        if (s.status === 'error') {
          setError(s.error ?? 'チェックに失敗しました。');
        } else if (s.result) {
          setResult(s.result);
        } else {
          setError('結果がまだ準備できていません。');
        }
      } catch (e) {
        if (alive) setError(e instanceof Error ? e.message : String(e));
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [checkId]);

  if (loading) return <div className="loading">結果を読み込み中…</div>;
  if (error)
    return (
      <div className="page">
        <div className="alert alert--error">{error}</div>
        <Link className="btn" to="/admin/check">
          チェック画面へ戻る
        </Link>
      </div>
    );
  if (!result) return null;

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>チェック結果</h1>
          <a
            className="result-url"
            href={result.finalUrl ?? result.url}
            target="_blank"
            rel="noreferrer noopener"
          >
            {result.finalUrl ?? result.url}
          </a>
          <div className="muted small">計測: {formatDate(result.checkedAt)}</div>
        </div>
        <Link className="btn" to="/admin/check">
          + 新規チェック
        </Link>
      </div>

      <div className="result-top card">
        <div className="result-total">
          <ScoreBadge score={result.totalScore} size="lg" showLabel />
          <div className="result-total__meta">
            <div className="result-total__title">総合スコア</div>
            {result.capped && (
              <div className="cap-badge">
                重要項目の指摘によりスコア上限が適用されています
                {result.rawScore != null && `（素点 ${result.rawScore}）`}
              </div>
            )}
            <div className="cwv-source">
              Core Web Vitals: {CWV_SOURCE_LABEL[result.cwvSource]}
            </div>
          </div>
        </div>

        <div className="result-cats">
          {result.categoryScores.map((c) => (
            <div
              key={c.category}
              className="cat-chip"
              title={c.available === false ? c.unavailableReason : undefined}
            >
              <span className="cat-chip__label">{c.label}</span>
              {c.available === false ? (
                <span className="cat-chip__score cat-chip__score--na">測定不可</span>
              ) : (
                <span
                  className="cat-chip__score"
                  style={{ color: scoreColor(c.score) }}
                >
                  {c.score}
                </span>
              )}
              <span className="cat-chip__pts muted small">
                {c.available === false ? '対象外' : `${c.weightedPoints}/${c.maxPoints}`}
              </span>
            </div>
          ))}
        </div>
      </div>

      <div className="result-grid">
        <div className="card">
          <h2>カテゴリ別評価</h2>
          <ScoreRadar categoryScores={result.categoryScores} />
        </div>
        <div className="card">
          <h2>課題リスト</h2>
          <IssueList items={result.items} />
        </div>
      </div>

      <div className="rubric-note">
        ※ コンテンツ品質などの評価は、Google公式ガイド（社内ナレッジ版）を基にした
        ルーブリック採点です。Googleの実際の評価・検索順位そのものではありません。
      </div>
    </div>
  );
}
